import { Component, JSX } from "react";
import { RouteComponentProps } from "react-router-dom";

import moment from "moment";

import { ReactorConfiguration } from "@app/arc";
import ArcReactor from "@app/projects/ArcReactor";
import { CountyProjectContainerState, CountyProjectContext } from "@app/projects/CountyProjectContext";
import {
  AttestationRateDays,
  NetworkAssessmentAnalysisType,
  ProjectRunConfigurationType,
  TimeFrequency
} from "@app/projects/enumerators";
import { shouldSwitchProjectRunConfigurationsInCompareMode } from "@app/projects/helpers";
import { ProjectModule } from "@app/projects/literals";
import { AccuracyAggregate } from "@app/projects/network-assessment/accuracy/accuracyAggregate";
import { AdequacyAggregate } from "@app/projects/network-assessment/adequacy";
import ChartTooltipButton from "@app/projects/network-assessment/ChartTooltipButton";
import { CompositeAggregates } from "@app/projects/network-assessment/composite/CompositeAggregates";
import ProjectOptions from "@app/projects/projectOptions";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { AccuracyTrendChartItem, AdequacyTrendChartItem, ResultRegion } from "@app/projects/results/common";
import Velvet from "@app/projects/Velvet";
import { RegionGranularity } from "@app/region-sets";
import { FlyoutLink } from "@app/shared/Flyouts";
import { formatDateCustom, formatDateTime } from "@app/shared/formatDateTime";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { formatAccuracyBandPercent } from "@app/shared/utility/formatAccuracyBandPercent";
import { TemplateMetadata } from "@app/templates";
import { AccuracyTemplate, AccuracyTemplateContext } from "@app/templates/accuracy";
import { VelvetColors, VelvetConfiguration, VelvetItem, VelvetOptions } from "@app/velvet";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { VelvetLabel } from "@app/velvet/types";

type ChartsProps =
  RouteComponentProps<unknown> & {
  module: ProjectModule;
  attestationRateDays: AttestationRateDays;
}

class Charts extends Component<ChartsProps> {
  constructor(props: ChartsProps) {
    super(props);
  }

  public render() {
    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <CountyProjectContext.Consumer>
            {({
                accuracyResult,
                compareToAccuracyResult,
                adequacyResult,
                compareToAdequacyResult,
                compareToRunConfiguration,
                compareToRunConfigurationTimestamp,
                setRunConfiguration,
                loading,
                loadingResults,
                runConfiguration,
                runConfigurationTimestamp,
                accuracyTrendChartData,
                adequacyTrendChartData,
                project,
                templateMetadata,
                isDualMembershipMode,
                adequacyTrendChartDataDualMembership,
                updateRunConfigurationComment,
                updateRunConfigurationState,
                runFrequency,
            }) => {
              return (((loading || loadingResults) && !project.isReadOnly) || !runConfiguration)
                ? (<span className="skeleton shimmer"><SkeletonCharts
                  isComparingResults={!!compareToRunConfiguration}/></span>)
                : (
                  this.renderTrendCharts(
                    project,
                    accuracyResult && accuracyResult.aggregateSummary || null,
                    compareToAccuracyResult && compareToAccuracyResult.aggregateSummary || null,
                    adequacyResult && adequacyResult.aggregateSummary || null,
                    compareToAdequacyResult && compareToAdequacyResult.aggregateSummary || null,
                    compareToRunConfiguration,
                    compareToRunConfigurationTimestamp,
                    setRunConfiguration,
                    updateRunConfigurationState,
                    runConfiguration,
                    runConfigurationTimestamp,
                    templateMetadata,
                    accuracyTemplate,
                    adequacyTrendChartData,
                    adequacyTrendChartDataDualMembership,
                    accuracyTrendChartData,
                    this.props.module,
                    isDualMembershipMode,
                    runFrequency,
                    updateRunConfigurationComment
                  )
                );
            }}
          </CountyProjectContext.Consumer>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private renderTrendCharts(
    project: ProjectOptions,
    accuracyAggregate: AccuracyAggregate,
    compareToAccuracyAggregate: AccuracyAggregate,
    adequacyAggregate: AdequacyAggregate,
    compareToAdequacyAggregate: AdequacyAggregate | undefined,
    compareToRunConfiguration: ProjectRunConfigurationResult | undefined,
    compareToRunConfigurationTimestamp: moment.Moment,
    setRunConfiguration: (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean) => Promise<Partial<CountyProjectContainerState>>,
    updateRunConfigurationState: (newState: CountyProjectContainerState) => void,
    runConfiguration: ProjectRunConfigurationResult,
    runConfigurationTimestamp: moment.Moment,
    templateMetadata: TemplateMetadata,
    accuracyTemplate: AccuracyTemplate,
    adequacyTrendChartData: AdequacyTrendChartItem[],
    adequacyTrendChartDataDualMembership: AdequacyTrendChartItem[],
    accuracyTrendChartData: AccuracyTrendChartItem[],
    module: ProjectModule,
    isDualMembershipMode: boolean,
    runFrequency: TimeFrequency,
    updateRunConfigurationComment: (comment: string, runConfigurationId: number) => void
  ) {

    //temporarily disable result comparison for Accuracy until further notice
    if (this.props.module === "Accuracy") {
      compareToRunConfiguration = null;
      isDualMembershipMode = false;
    }

    if (!compareToRunConfigurationTimestamp) {
      compareToRunConfigurationTimestamp = moment.utc(compareToRunConfiguration && compareToRunConfiguration.dateCreated);
    }
    if (!runConfigurationTimestamp) {
      runConfigurationTimestamp = moment.utc(runConfiguration && runConfiguration.dateCreated);
    }

    const firstRunConfiguration = compareToRunConfiguration && !isDualMembershipMode && shouldSwitchProjectRunConfigurationsInCompareMode(runConfiguration, compareToRunConfiguration)
      ? compareToRunConfiguration
      : runConfiguration;
    const firstAdequacyAggregate = firstRunConfiguration === compareToRunConfiguration ? compareToAdequacyAggregate : adequacyAggregate;
    const firstAccuracyAggregate = firstRunConfiguration === compareToRunConfiguration ? compareToAccuracyAggregate : accuracyAggregate;

    // Then the second.
    const secondRunConfiguration = firstRunConfiguration === compareToRunConfiguration ? runConfiguration : compareToRunConfiguration;
    const secondAdequacyAggregate = firstRunConfiguration === compareToRunConfiguration ? adequacyAggregate : compareToAdequacyAggregate;
    const secondAccuracyAggregate = firstRunConfiguration === compareToRunConfiguration ? accuracyAggregate : compareToAccuracyAggregate;

    const isComparingResults = !!compareToRunConfiguration;
    const compareToRunConfigurationId = isComparingResults && compareToRunConfiguration ? compareToRunConfiguration.projectRunConfigurationId : null;
    const runConfigurationId = runConfiguration ? runConfiguration.projectRunConfigurationId : null;

    const arcReactorClasses = isComparingResults
      ? "col-xl-2 col-6 order-1 overview-trending-compare"
      : "col-lg-3 col-12 position-relative";
    const trendChartClasses = isComparingResults
      ? "col-xl-8 col-12 order-3 order-xl-2 overview-trending-compare"
      : "col-lg-9 col-12";

    return (
      <>
        <div className="row">
          <div className={`overview-trending-charts ${arcReactorClasses}`}>
            {this.renderArcReactor(project, firstRunConfiguration, secondRunConfiguration, firstAccuracyAggregate, firstAdequacyAggregate, templateMetadata, adequacyTrendChartData, isComparingResults)}
          </div>
          {((isComparingResults) || null) &&
            <div className={`overview-trending-charts col-xl-2 col-6 order-2 order-xl-3 overview-trending-compare`}>
              {this.renderArcReactor(project, secondRunConfiguration, firstRunConfiguration, secondAccuracyAggregate, secondAdequacyAggregate, templateMetadata, adequacyTrendChartData, isComparingResults)}
            </div>
          }
          <div className={`overview-trending-charts trend-chart overflow-hidden ${trendChartClasses}`}>
            <div className="chart">
              <Velvet
                config={
                  this.velvetConfiguration(
                    project,
                    accuracyTemplate,
                    module,
                    accuracyTrendChartData,
                    adequacyTrendChartData,
                    adequacyTrendChartDataDualMembership,
                    isComparingResults,
                    runConfigurationId,
                    isDualMembershipMode,
                    runConfigurationTimestamp,
                    setRunConfiguration,
                    updateRunConfigurationState,
                    runFrequency,
                    compareToRunConfigurationId,
                    compareToRunConfigurationTimestamp
                  )}
                attestationRateDays={this.props.attestationRateDays}
                projectRunConfigurations={project.projectRunConfigurations}
              />
              <div className="velvet-tooltip">
                <ChartTooltipButton projectId={project.projectId} updateRunConfigurationComment={updateRunConfigurationComment} />
                <div className="velvet-tooltip__data"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <hr className="mtb0" />
          </div>
        </div>
      </>
    );
  }

  private renderArcReactor(
    project: ProjectOptions,
    runConfiguration: ProjectRunConfigurationResult,
    compareToRunConfiguration: ProjectRunConfigurationResult,
    accuracyAggregate: AccuracyAggregate,
    adequacyAggregate: AdequacyAggregate,
    templateMetadata: TemplateMetadata,
    trendChartData: AdequacyTrendChartItem[],
    isComparingResults: boolean
  ) {
    const runConfigurationTimestamp = moment.utc(runConfiguration.dateCreated);

    const aggregateRegionSet = this.props.module === "Accuracy" ?
      accuracyAggregate && accuracyAggregate.regionSetId
        ? this.getSubRegionById(runConfiguration.serviceArea, accuracyAggregate.regionSetId)
        : null

      :
      adequacyAggregate && adequacyAggregate.regionSetId
        ? this.getSubRegionById(runConfiguration.serviceArea, adequacyAggregate.regionSetId)
        : null;
    const regionCounts = this.regionCounts(aggregateRegionSet, new RegionCounts(0, 0, 0));
    const hasRegions = !!regionCounts.CountyCount || !!regionCounts.ZipCount;
    const showOpportunityAnalysisInfo =
      hasRegions &&
      runConfiguration &&
      runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.Opportunity &&
      templateMetadata &&
      templateMetadata.useMedicareRatebook &&
      adequacyAggregate &&
      !adequacyAggregate.specialtyGroupCode;
    const trendChartItem = trendChartData ? trendChartData.find((t) => t && t.projectRunConfigurationId && (t.projectRunConfigurationId === runConfiguration.projectRunConfigurationId || t.projectRunConfigurationId === runConfiguration.companionProjectRunConfigurationId)) : null;
    // TODO: Bubble up to where appropriate

    const compositeAggregates: CompositeAggregates = {
      accuracyAggregate,
      adequacyAggregate,
      attestationAggregate: null,
      compositeIndex: Math.round(((adequacyAggregate && adequacyAggregate.adequacyIndex || 0) + (accuracyAggregate && accuracyAggregate.accuracyIndex || 0)) / 2 * 100) / 100,
      compositeOverride: [accuracyAggregate, adequacyAggregate].filter((ag) => !!ag).length > 1 ? undefined : "adequacy", // TODO: Don't rely on adequacy.
    };

    const flyoutTitle = (() => {
      switch (trendChartItem?.itemType) {
        case TrendChartItemType.Termed:
          return "Termed Adequacy Details";
        case TrendChartItemType.Projected:
          return "Projected Adequacy Details";
        case TrendChartItemType.ProjectedTermed:
          return "Projected-Termed Adequacy Details";
        default:
          return `Adequacy Details as of ${formatDateTime(runConfigurationTimestamp)}`;
      }
    })();

    return (
      <div className="arc-reactor">
        <div className="text-center mb15">
          <strong>
            {
              (runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ||
                runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) &&
              !!runConfiguration.companionProjectRunConfigurationId &&
              (isComparingResults && compareToRunConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.Standard &&
                compareToRunConfiguration.projectRunConfigurationType !== runConfiguration.projectRunConfigurationType) ?
                runConfiguration.memberSetAlias :
                trendChartItem
                  ? this.getRunConfigurationLabel(trendChartItem.itemType, trendChartItem.runFrequency, runConfigurationTimestamp)
                  : <>&nbsp;</>
            }
          </strong>
        </div>
        {(hasRegions && !!trendChartItem && this.props.module === "Adequacy") &&
          <div className="text-end reactor-description-icon">
            <TooltipWrapper
              text={`Show Adequacy${showOpportunityAnalysisInfo ? " and Opportunity Analysis" : ""} Details`}>
              <FlyoutLink
                content={{
                  body: this.renderAdequacyDetails(runConfigurationTimestamp, adequacyAggregate, showOpportunityAnalysisInfo),
                  title: flyoutTitle,
                }}
                testId="adequacy-details-flyout-link"
                size="40"
                classNames="qes-button-secondary p-0"
              >
                <QesIcon iconName={QesIcons.InfoCircle} description="More details" className="align-text-top me-1" />
              </FlyoutLink>
            </TooltipWrapper>
          </div>
        }
        <ArcReactor config={this.reactorConfiguration(project, compositeAggregates)} />
        {runConfiguration.serviceArea &&
          <div className={isComparingResults ? "dual-region-count-trend" : "region-count-trend"}>
            {this.renderArcReactorDescription(regionCounts, runConfiguration.networkAssessmentAnalysisType, adequacyAggregate ? adequacyAggregate.specialtyGroupDisplayName : "")}
          </div>}
      </div>
    );
  }

  private getRunConfigurationLabel(itemType: TrendChartItemType, runFrequency: TimeFrequency, runConfigurationTimestamp: moment.Moment): string {
    switch (itemType) {
      case TrendChartItemType.Termed:
        return "Termed";
      case TrendChartItemType.Projected:
        return "Projected";
      case TrendChartItemType.ProjectedTermed:
        return "Projected-Termed";
      case TrendChartItemType.OlderPublished:
      case TrendChartItemType.LatestPublished:
      case TrendChartItemType.Unpublished:
      default:
        switch (runFrequency) {
          case TimeFrequency.All:
            return runConfigurationTimestamp === undefined ? "" : formatDateTime(runConfigurationTimestamp.utc(true));
          case TimeFrequency.Monthly:
          default:
            return runConfigurationTimestamp === undefined ? "" : formatDateCustom(runConfigurationTimestamp.utc(true), "MMMM YYYY");
        }
    }
  }

  private reactorConfiguration(project: ProjectOptions, compositeAggregates: CompositeAggregates): ReactorConfiguration {
    if (compositeAggregates) {
      const ca = compositeAggregates;

      const config: ReactorConfiguration = {
        accuracy: null,
        adequacy: null,
        attestation: null,
        composite: null,
      };

      switch (this.props.module) {
        case "Accuracy":
          if (!ca.accuracyAggregate || !ca.accuracyAggregate.bands) {
            return null;
          }
          config.accuracy = {
            bands: ca.accuracyAggregate.bands.map((b) => ({
              color: b.color,
              name: b.name,
              value: b.inspectionCount * b.weightSummation,
            })).filter((b) => b.value > 0),
            indeterminantText: "N/A",
            index: ca.accuracyAggregate.accuracyIndex,
            isIndeterminant: ca.accuracyAggregate.providerCount === 0,
          };

          let attestationPercent: number = null;

          if (project.showAttestation && ca.adequacyAggregate) {
            //adequacy/accuracy & adequacy-only projects
            attestationPercent = this.props.attestationRateDays === AttestationRateDays.Ninety ? ca.accuracyAggregate.ninetyDayAttestedPercent : ca.adequacyAggregate.recentlyAttestedUniqueNPIPercent;
          } else {
            //accuracy-only projects
            attestationPercent = this.props.attestationRateDays === AttestationRateDays.Ninety ? ca.accuracyAggregate.ninetyDayAttestedPercent : ca.accuracyAggregate.recentlyAttestedPercent;
          }

          config.attestation = {
            attestationRateDays: this.props.attestationRateDays,
            green: attestationPercent || 0,
            index: attestationPercent || 0,
            isPercent: true,
            red: 0,
            yellow: 0,
          };

          config.compositeOverride = "accuracy";
          break;
        case "Adequacy":
          if (!ca.adequacyAggregate) {
            return null;
          }
          config.adequacy = {
            blue: ca.adequacyAggregate.percentPassByException || 0,
            green: ca.adequacyAggregate.percentPassBoth || 0,
            index: ca.adequacyAggregate.adequacyIndex !== undefined && ca.adequacyAggregate.adequacyIndex !== null ? ca.adequacyAggregate.adequacyIndex : "N/A",
            projected: ca.adequacyAggregate.projectedPercentPassBoth || null,
            projectedIndex: ca.adequacyAggregate.projectedAdequacyIndex || null,
            red: ca.adequacyAggregate.percentPassNone || 0,
            yellow: ((ca.adequacyAggregate.percentPassAccessOnly || 0) + ca.adequacyAggregate.percentPassServicingOnly) || 0,
          };

          if (project.showAttestation) {
            config.attestation = {
              green: ca.adequacyAggregate.recentlyAttestedUniqueNPIPercent || 0,
              index: ca.adequacyAggregate.recentlyAttestedUniqueNPIPercent || 0,
              isPercent: true,
              red: 0,
              yellow: 0,
            };
          }

          config.compositeOverride = "adequacy";
          break;
        default:
          break;
      }

      return config;
    }

    return null;
  }

  /**
   * Builds the configuration necessary to render a Velvet (Trend Chart) component.
   * @param project The project we're operating under.
   * @param accuracyTemplate The accuracy template, if any.
   * @param adequacyTrendChartItems The set of API items that have been retrieved.
   * @param isComparingResults Whether comparison is active.
   * @param projectRunConfigurationId The currently active run configuration.
   * @param projectRunConfigurationTimestamp The run configuration's timestamp.
   * @param setRunConfiguration A callback for getting the data to change the active run configuration, or to directly change the active run configuration.
   * @param updateRunConfigurationState A function for changing the active Project Container state (and therefore active run configuration)
   * @param compareToRunConfigurationId The currently active comparison run configuration. Optional.
   * @param compareToRunConfigurationTimestamp The currently active comparison run configuration's timestamp.
   */
  private velvetConfiguration(
    project: ProjectOptions,
    accuracyTemplate: AccuracyTemplate,
    module: ProjectModule,
    accuracyTrendChartItems: AccuracyTrendChartItem[],
    adequacyTrendChartItems: AdequacyTrendChartItem[],
    adequacyTrendChartItemsDualMembership: AdequacyTrendChartItem[],
    isComparingResults: boolean,
    projectRunConfigurationId: number,
    isDualMembershipMode: boolean,
    projectRunConfigurationTimestamp: moment.Moment,
    setRunConfiguration:
    (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean) => Promise<Partial<CountyProjectContainerState>>,
    updateRunConfigurationState: (newState: CountyProjectContainerState) => void,
    runFrequency: TimeFrequency,
    compareToRunConfigurationId?: number,
    compareToRunConfigurationTimestamp?: moment.Moment,
  ): VelvetConfiguration {
    const options: VelvetOptions = {
      fixedColumnWidths: true,
      showAdvancedComparison: project.showAdvancedComparison,
      showAttestation: project.showAttestation,
    };
    let use0To100YAxisBounds = false;
    let primaryMembershipAlias: string;
    let secondaryMembershipAlias: string;
    if (isDualMembershipMode) {
      // 9 characters and an ellipsis is about the limit of what fits in a velvet legend column
      primaryMembershipAlias = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === projectRunConfigurationId).memberSetAlias;
      primaryMembershipAlias = primaryMembershipAlias.length > 9 ? `${primaryMembershipAlias.substr(0, 9)}...` : primaryMembershipAlias;
      const companion = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === compareToRunConfigurationId);
      secondaryMembershipAlias = companion && companion.memberSetAlias;
      secondaryMembershipAlias = secondaryMembershipAlias && secondaryMembershipAlias.length > 9 ? `${secondaryMembershipAlias.substr(0, 9)}...` : secondaryMembershipAlias;
    }

    let labels: VelvetLabel[] = [];
    let items: VelvetItem[] = null;
    let supplementaryItems: VelvetItem[] = null;
    const dualMembershipEnabled = isDualMembershipMode && !!adequacyTrendChartItemsDualMembership && !!adequacyTrendChartItemsDualMembership.length;
    const showDualAttestation = project.showAttestation && dualMembershipEnabled;
    switch (module) {
      case "Adequacy":
        if (project.isReadOnly && !project.resource.isArchived) {
          adequacyTrendChartItems = adequacyTrendChartItems.filter((item) => ![TrendChartItemType.Projected, TrendChartItemType.Termed, TrendChartItemType.ProjectedTermed].includes(item.itemType));
          adequacyTrendChartItemsDualMembership = adequacyTrendChartItemsDualMembership.filter((item) => ![TrendChartItemType.Projected, TrendChartItemType.Termed, TrendChartItemType.ProjectedTermed].includes(item.itemType));
        } else if (!project.showAdvancedComparison) {
          adequacyTrendChartItems = adequacyTrendChartItems.filter((item) => ![TrendChartItemType.OlderPublished].includes(item.itemType));
          adequacyTrendChartItemsDualMembership = adequacyTrendChartItemsDualMembership.filter((item) => ![TrendChartItemType.OlderPublished].includes(item.itemType));
        }
        const adequacyLabelNames = {
          adequacyGapsMajor: "Adequacy Gaps (major)",
          adequacyGapsMinor: "Adequacy Gaps (minor)",
          primaryAdequacyIndex: `Adequacy Index${primaryMembershipAlias ? ` (${primaryMembershipAlias})` : ""}`,
          primaryAttestation: `Percent Attested${!!primaryMembershipAlias ? ` (${primaryMembershipAlias})` : ""}`,
          secondaryAdequacyIndex: `Adequacy Index (${secondaryMembershipAlias})`,
          secondaryAttestation: `Percent Attested (${secondaryMembershipAlias})`,
        };
        labels = [
          dualMembershipEnabled && {
            color: VelvetColors.GreenDark,
            name: adequacyLabelNames.secondaryAdequacyIndex,
            orientation: "left",
            tooltip: "Supplementary Membership",
            type: "line",
          } || null,
          {
            breakAfter: dualMembershipEnabled,
            color: VelvetColors.Green,
            name: adequacyLabelNames.primaryAdequacyIndex,
            orientation: "left",
            tooltip: "",
            type: "line",
          },
          showDualAttestation && {
            color: VelvetColors.BlueLight,
            name: adequacyLabelNames.secondaryAttestation,
            orientation: "left",
            tooltip: "Supplementary Membership",
            type: "line",
          } || null,
          project.showAttestation && {
            breakAfter: true,
            color: VelvetColors.Blue,
            name: adequacyLabelNames.primaryAttestation,
            orientation: "left",
            tooltip: "",
            type: "line",
          } || null,
          {
            color: VelvetColors.Yellow,
            name: adequacyLabelNames.adequacyGapsMinor,
            orientation: "left",
            tooltip: "Meets only Access or Servicing Provider requirements.",
            type: "bar",
          },
          {
            breakAfter: true,
            color: VelvetColors.Red,
            name: adequacyLabelNames.adequacyGapsMajor,
            orientation: "left",
            tooltip: "Does not meet any Adequacy requirements.",
            type: "bar",
          },
          // { color: VelvetColors.Blue, orientation: "left", name: "Adequacy Exceptions", tooltip: "Exception assigned to gap.", type: "bar" },
        ];
        labels = labels.filter((x) => !!x);

        if (adequacyTrendChartItems && adequacyTrendChartItems.length > 0) {
          items = adequacyTrendChartItems.map<VelvetItem>((item) => ({
            barGroups: [
              {
                colorOrder: [VelvetColors.Red, VelvetColors.Yellow, VelvetColors.Blue],
                items: [
                  { color: VelvetColors.Red, value: item.noneMetCount },
                  { color: VelvetColors.Yellow, value: item.oneMetCount },
                  { color: VelvetColors.Blue, value: 0 },
                ],
              },
            ],
            disabled: item.disabled || item.isFillMonth,
            itemType: item.itemType,
            linePoints: [
              { color: VelvetColors.Green, value: item.adequacyIndex },
              project.showAttestation && { color: VelvetColors.Blue, value: item.recentlyAttestedUniqueNPIPercent },
            ].filter((x) => !!x),
            projectRunConfigurationId: item.projectRunConfigurationId,
            publishComment: project.projectRunConfigurations.find(prc => prc.projectRunConfigurationId === item.projectRunConfigurationId)?.publishComment,
            runFrequency: item.runFrequency,
            timestamp: moment.utc(item.date),
            tooltip: this.getVelvetTooltip(item, project, module, labels, dualMembershipEnabled, adequacyLabelNames),
          }));
        }
        if (dualMembershipEnabled) {
          supplementaryItems = adequacyTrendChartItemsDualMembership.map<VelvetItem>((supplementaryItem) => ({
            barGroups: [
              {
                colorOrder: [VelvetColors.Red, VelvetColors.Yellow, VelvetColors.Blue],
                items: [
                  { color: VelvetColors.Red, value: supplementaryItem.noneMetCount },
                  { color: VelvetColors.Yellow, value: supplementaryItem.oneMetCount },
                  { color: VelvetColors.Blue, value: 0 },
                ],
              },
            ],
            disabled: supplementaryItem.disabled,
            itemType: supplementaryItem.itemType,
            linePoints: [
              { color: VelvetColors.GreenDark, value: supplementaryItem.adequacyIndex },
              showDualAttestation && {
                color: VelvetColors.BlueLight,
                value: supplementaryItem.recentlyAttestedUniqueNPIPercent
              },
            ],
            projectRunConfigurationId: supplementaryItem.projectRunConfigurationId,
            publishComment: project.projectRunConfigurations.find(prc => prc.projectRunConfigurationId === supplementaryItem.projectRunConfigurationId)?.publishComment,
            runFrequency: supplementaryItem.runFrequency,
            timestamp: moment.utc(supplementaryItem.date),
            tooltip: this.getVelvetTooltip(supplementaryItem, project, module, labels, dualMembershipEnabled, adequacyLabelNames),
          }));

          items.forEach((item) => {
            item.companionRunConfigurationId = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === item.projectRunConfigurationId)?.companionProjectRunConfigurationId;
          });
        }
        break;
      case "Accuracy":
        use0To100YAxisBounds = true;
        const ls: VelvetLabel[] = [
          {
            color: "#00a46c",
            name: "Accuracy Index",
            orientation: "left",
            tooltip: "",
            type: "line"
          },
          ...(project.showAttestation
            ? [{
              breakAfter: true,
              color: VelvetColors.Blue,
              name: "Percent Attested",
              orientation: "left",
              tooltip: "",
              type: "line",
            } as VelvetLabel]
            : []),
        ];
        if (project.isReadOnly && !project.resource.isArchived) {
          accuracyTrendChartItems = accuracyTrendChartItems.filter((item) => ![TrendChartItemType.Projected, TrendChartItemType.Termed, TrendChartItemType.ProjectedTermed].includes(item.itemType));
        }

        if (accuracyTemplate && accuracyTemplate.scoring) {
          accuracyTemplate.scoring.bandGroups.forEach((bandGroup) => {
            bandGroup.bands.forEach((band, i) => {
              ls.push({
                breakAfter: i === bandGroup.bands.length - 1,
                color: band.bandColor,
                name: band.name,
                orientation: "left" as "left" | "right",
                tooltip: `${bandGroup.name} - ${band.name}`,
                type: "bar",
              });
            });
          });
        }

        labels = ls;
        if (accuracyTrendChartItems && accuracyTrendChartItems.length > 0) {
          items = accuracyTrendChartItems.map<VelvetItem>((item) => ({
            barGroups: [
              {
                colorOrder: item.bands.map<string>((band) => band.color),
                items: item.bands.map<{ color: string; value: number; }>((band) => ({
                  color: band.color,
                  value: band.value,
                })),
                mode: "percentage",
              },
            ],
            disabled: !project.projectRunConfigurations.some((prc) => prc.dateCreated === item.date),
            itemType: item.itemType,
            linePoints: [
              { color: "#00a46c", value: item.accuracyIndex },
              ...(project.showAttestation ?
                [{
                  color: VelvetColors.Blue,
                  toolTipBody: `<span>${this.getAttestationPercent(item, this.props.attestationRateDays)}<br /> Percentage is based on a ${this.props.attestationRateDays}-day attestation rate</span>`,
                  value: this.getAttestationPercent(item, this.props.attestationRateDays)
                }] :
                []),
            ],
            projectRunConfigurationId: item.projectRunConfigurationId,
            publishComment: project.projectRunConfigurations.find(prc => prc.projectRunConfigurationId === item.projectRunConfigurationId)?.publishComment,
            runFrequency: item.runFrequency,
            timestamp: moment.utc(item.date),
            tooltip: this.getVelvetTooltip(item, project, module, labels, isDualMembershipMode, null),
          }));
        }
        break;
    }

    const currentConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === projectRunConfigurationId);

    return {
      compareToRunConfigurationId,
      compareToRunConfigurationTimestamp,
      dualMembershipConfigurationId: currentConfiguration ? currentConfiguration.companionProjectRunConfigurationId : null,
      isComparingResults,
      isDualMembership: isDualMembershipMode,
      items,
      labels,
      module,
      options,
      projectId: project.projectId,
      projectRunConfigurationId,
      projectRunConfigurationTimestamp,
      setRunConfiguration,
      supplementaryItems,
      updateRunConfigurationState,
      use0To100YAxisBounds,
    };
  }

  private getVelvetTooltip = (
    item: AdequacyTrendChartItem | AccuracyTrendChartItem,
    project: ProjectOptions,
    module: ProjectModule,
    labels: VelvetLabel[],
    isDualMembershipMode: boolean,
    adequacyLabelNames: { [key: string]: string },
  ): string => {
    const itemRunConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === item.projectRunConfigurationId);
    
    // in the edge case all targeted/excluded providers are removed, and the projected timeslice disappears,
    // it's possible for it to come back in the trend graph api response but not the project api response,
    // so there'll be no match in the .find call above
    if (!itemRunConfiguration) {
      return "";
    }

    if ((item as AdequacyTrendChartItem).disabled
      || !project.projectRunConfigurations.some((prc) => prc.dateCreated === item.date)) {
      return "<span>There is no data for this selection.</span>";
    }
    // necessary because accuracy bands are dynamic and template-specific
    const colorsToLabels: { [color: string]: { name: string, shape: "line" | "bar" } } = {};
    // necessary because adequacy only has the name in the item
    const namesToLabels: { [name: string]: { color: string, shape: "line" | "bar" } } = {};
    labels.forEach((l) => {
      colorsToLabels[l.color.toLowerCase()] = {
        name: l.name,
        shape: l.type,
      };
      namesToLabels[l.name] = {
        color: l.color.toLowerCase(),
        shape: l.type,
      };
    });
    // The color for "Enriched" was changed recently enough
    // that runs from before the change still exist and still provide the older color
    colorsToLabels["#4fa0ff"] = ({
      name: "Enriched",
      shape: "bar",
    });

    let tooltipHtmlString = !!itemRunConfiguration?.publishComment || !!itemRunConfiguration?.datePublished ? `<span class="velvet-tooltip__comment">${itemRunConfiguration?.publishComment || "&nbsp;"}</span>` : "";
    if (isDualMembershipMode) {
      tooltipHtmlString += `<span class="velvet-tooltip__membership">${itemRunConfiguration.memberSetAlias}</span>`;
    }

    tooltipHtmlString += "<dl>";

    if (module === "Adequacy") {
      const isPrimary = itemRunConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.Standard
        || itemRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary;
      const adequacyItem = item as AdequacyTrendChartItem;
      if (project.showAttestation && adequacyItem.recentlyAttestedUniqueNPIPercent >= 0) {
        tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
          ${this.getVelvetTooltipLabelShape(namesToLabels[adequacyLabelNames[isPrimary ? "primaryAttestation" : "secondaryAttestation"]])}
          Percent Attested</dt>
          <dd>${adequacyItem.recentlyAttestedUniqueNPIPercent}%</dd></div>
        `;
      }

      tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
        ${this.getVelvetTooltipLabelShape(namesToLabels[adequacyLabelNames[isPrimary ? "primaryAdequacyIndex" : "secondaryAdequacyIndex"]])}
        Adequacy Index</dt>
        <dd>${(adequacyItem).adequacyIndex}</dd></div>
       `;
      tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
        ${this.getVelvetTooltipLabelShape(namesToLabels[adequacyLabelNames["adequacyGapsMajor"]])}
        Adequacy Gaps (major)</dt>
        <dd>${adequacyItem.noneMetCount.toLocaleString()}</dd></div>
      `;
      tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
        ${this.getVelvetTooltipLabelShape(namesToLabels[adequacyLabelNames["adequacyGapsMinor"]])}
        Adequacy Gaps (minor)</dt>
        <dd>${adequacyItem.oneMetCount.toLocaleString()}</dd></div>
      `;
    } else if (module === "Accuracy") {
      const accuracyItem = item as AccuracyTrendChartItem;
      const totalCount = accuracyItem.bands.reduce((total, current) => {
        return total += current.value;
      }, 0);
      if (project.showAttestation) {
        tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
          ${this.getVelvetTooltipLabelShape(namesToLabels["Percent Attested"])}
          Percent Attested
          ${`<br /><span style="margin-left: 1.25rem;">${!!this.props.attestationRateDays ? `(${this.props.attestationRateDays}-day rate)` : "(365-day rate)"}</span></dt>`}
          <dd>${(this.props.attestationRateDays === AttestationRateDays.Ninety ? accuracyItem.ninetyDayAttestedUniqueNPIPercent : accuracyItem.recentlyAttestedUniqueNPIPercent) || 0}%</dd></div>
        `;
      }
      tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
        ${this.getVelvetTooltipLabelShape(namesToLabels["Accuracy Index"])}
        Accuracy Index</dt>
        <dd>${accuracyItem.accuracyIndex}</dd></div>
      `;
      accuracyItem.bands.forEach((band) => {
        tooltipHtmlString += `<div class="velvet-tooltip__definition"><dt>
          ${this.getVelvetTooltipLabelShape(namesToLabels[colorsToLabels[band.color.toLowerCase()]?.name])}
          ${colorsToLabels[band.color.toLowerCase()]?.name}</dt>
          <dd>${this.getAccuracyPercentageString(totalCount, band.value)}</dd></div>
        `;
      });
    }

    tooltipHtmlString += "</dl>";
    return tooltipHtmlString;
  }

  private getVelvetTooltipLabelShape(label: { shape: "bar" | "line", color: string }) {
    return `<span class="velvet-tooltip__legend-icon ${label?.shape}" style="background-color: ${label?.color}"></span>`;
  }

  private getAccuracyPercentageString(totalCount: number, currentCount: number) {
    if (totalCount === 0) {
      return "0%";
    }

    return formatAccuracyBandPercent((currentCount / totalCount) * 100);
  }

  private renderAdequacyDetails(
    runConfigurationTimestamp: moment.Moment,
    adequacySummary: AdequacyAggregate,
    showOpportunityAnalsisInfo: boolean) {

    if (showOpportunityAnalsisInfo) {
      return (
        <>
          {this.renderServiceAreaAdequacyDetails(adequacySummary)}
          {this.renderOpportunityAnalsisInfo(runConfigurationTimestamp, adequacySummary)}
        </>
      );
    }

    return this.renderServiceAreaAdequacyDetails(adequacySummary);
  }

  private renderServiceAreaAdequacyDetails(adequacySummary: AdequacyAggregate) {
    const percentPassBoth = adequacySummary.percentPassBoth ? adequacySummary.percentPassBoth.toFixed(2) : "0.00";
    const percentPassByException = adequacySummary.percentPassByException ? adequacySummary.percentPassByException.toFixed(2) : "0.00";
    const percentPassAccessOnly = adequacySummary.percentPassAccessOnly ? adequacySummary.percentPassAccessOnly.toFixed(2) : "0.00";
    const percentPassServicingOnly = adequacySummary.percentPassServicingOnly ? adequacySummary.percentPassServicingOnly.toFixed(2) : "0.00";
    const percentPassNone = adequacySummary.percentPassNone ? adequacySummary.percentPassNone.toFixed(2) : "0.00";

    return (
      <div>
        <div className="fs18 text-success">
          {`${percentPassBoth}% fully meet the adequacy requirements`}
        </div>
        <div className="fs18 text-info">
          {`${percentPassByException}% passing by approved exceptions`}
        </div>
        <div className="fs18 text-warning">
          {`${percentPassAccessOnly}% (${adequacySummary.accessOnlyMetCount} gap${adequacySummary.accessOnlyMetCount === 1 ? "" : "s"}) meet${adequacySummary.accessOnlyMetCount === 1 ? "s" : ""} only the access requirements`}
        </div>
        <div className="fs18 text-warning">
          {`${percentPassServicingOnly}% (${adequacySummary.servicingOnlyMetCount} gap${adequacySummary.servicingOnlyMetCount === 1 ? "" : "s"}) meet${adequacySummary.servicingOnlyMetCount === 1 ? "s" : ""} only the servicing provider requirements`}
        </div>
        <div className="fs18 text-danger">
          {`${percentPassNone}% (${adequacySummary.noneMetCount} gap${adequacySummary.noneMetCount === 1 ? "" : "s"}) do${adequacySummary.noneMetCount === 1 ? "es" : ""} not meet any adequacy requirements`}
        </div>
      </div>
    );
  }

  private renderOpportunityAnalsisInfo(runConfigurationTimestamp: moment.Moment, adequacySummary: AdequacyAggregate) {
    return (
      <div className="mt15">
        <div className="lead">
          {`Opportunity Analysis as of ${formatDateTime(runConfigurationTimestamp)}`}
        </div>
        <hr />
        <div className="fs18 ml15 text-success">
          {`$${adequacySummary.fourStarsGreaterAnnually ? adequacySummary.fourStarsGreaterAnnually.toLocaleString(undefined, { minimumFractionDigits: 2 }) : "0.00"} in 4+ Star Annual Opportunity`}
        </div>
        <div className="fs18 ml15 text-warning">
          {`${adequacySummary.penetration ? adequacySummary.penetration : "0.0"}% average Medicare Advantage penetration`}
        </div>
        <div className="fs18 ml15 text-danger">
          {`${adequacySummary.gapCount ? adequacySummary.gapCount : "0"} gap${adequacySummary.gapCount === 1 ? "" : "s"} requiring attention`}
        </div>
        <div className="mt15">
          <p className="text-muted">
            <small>
              <em>
                The financial information used in this analysis is compiled from the 2019 Medicare Advantage Rate Book
                for illustration purposes only in order to expose potential areas of expansion. Quest Analytics provides
                this data &apos;as-is&apos; and makes no claims as to the actual financial opportunity. The number of
                gaps does not represent the marketability of an area, but is a reflection on meeting the minimum time
                and distance requirements. Before pursuing an expansion into any new area, a thorough actuarial analysis
                and market feasibility study should be performed.
              </em>
            </small>
          </p>
        </div>
      </div>
    );
  }

  private renderArcReactorDescription(
    regionCounts: RegionCounts,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    specialtyGroupDescription: string) {
    let prefixText = <></>;

    if (networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.Opportunity) {
      if (!(regionCounts.CountyCount || regionCounts.ZipCount)) {
        return (
          <p>Opportunities for expansion have not been defined.</p>
        );
      }

      prefixText = <>Viewing opportunities for expansion:<br /></>;

    } else {
      if (!(regionCounts.CountyCount || regionCounts.ZipCount)) {
        return (
          <div className="text-center">
            <p>Service Area is currently undefined.</p>
          </div>
        );
      }

      prefixText = <>Viewing service area results for<br /></>;
    }

    if (specialtyGroupDescription) {
      prefixText = <>{prefixText}<strong>{specialtyGroupDescription} </strong><br /><strong>in </strong></>;
    }

    return (
      <p>{prefixText}<strong>{this.getRegionCountText(regionCounts)}</strong></p>
    );
  }

  private getRegionCountText(regionCounts: RegionCounts) {
    let regionText = "";
    if (regionCounts.CountyCount) {
      regionText = `${regionCounts.CountyCount.toString()} ${regionCounts.CountyCount > 1 ? "counties" : "county"}`;
    }
    if (regionCounts.ZipCount) {
      if (regionText) {
        regionText += " and ";
      }
      regionText += `${regionCounts.ZipCount.toString()} zip code${regionCounts.ZipCount > 1 ? "s" : ""}`;
    }
    if (regionCounts.StateCount) {
      if (regionText) {
        regionText += " in ";
      }
      regionText += `${regionCounts.StateCount.toString()} state${regionCounts.StateCount > 1 ? "s" : ""}`;
    }

    return regionText;
  }

  private getSubRegionById = (region: ResultRegion, regionSetId: number): ResultRegion => {
    if (region.regionSetId === regionSetId) {
      return region;
    }

    let subRegionById = null;
    for (const subRegion of region.subRegions) {
      if (subRegionById) {
        break; //exit once one is found
      }
      subRegionById = this.getSubRegionById(subRegion, regionSetId);
    }

    return subRegionById;
  }

  private regionCounts(region: ResultRegion, regionCounts: RegionCounts)
    : RegionCounts {

    if (!region) {
      return regionCounts;
    }

    switch (region.regionGranularity) {
      case RegionGranularity.State:
        if (region.subRegions.length) {
          ++regionCounts.StateCount;
          for (const subRegion of region.subRegions) {
            regionCounts = this.regionCounts(subRegion, regionCounts);
          }
        }
        break;
      case RegionGranularity.County:
        ++regionCounts.CountyCount;
        break;
      case RegionGranularity.Zip:
        ++regionCounts.ZipCount;
        break;
      case RegionGranularity.MultiRegion:
      case RegionGranularity.Unknown:
        //right now the MultiRegion granularity region set is converted to Unknown
        //granularity when the run configuration result objecy is created,
        //so Unknown granularity needs to be checked here unless we change our minds on that
        //decision
        for (const subRegion of region.subRegions) {
          regionCounts = this.regionCounts(subRegion, regionCounts);
        }
        break;
    }

    return regionCounts;
  }

  private getAttestationPercent(accuracyTrendChartItem: AccuracyTrendChartItem, attestationRateDays: AttestationRateDays): number {
    return attestationRateDays === AttestationRateDays.Ninety ?
      accuracyTrendChartItem.ninetyDayAttestedUniqueNPIPercent ?? 0 :
      accuracyTrendChartItem.recentlyAttestedUniqueNPIPercent ?? 0;
  }
}

class RegionCounts {
  public CountyCount: number;
  public StateCount: number;
  public ZipCount: number;

  constructor(countyCount: number, stateCount: number, zipCount: number) {
    this.CountyCount = countyCount;
    this.StateCount = stateCount;
    this.ZipCount = zipCount;
  }
}

function SkeletonCharts(props: { isComparingResults?: boolean }): JSX.Element {

  const arcReactorClasses = props.isComparingResults
    ? "col-xl-3 col-6 order-1 overview-trending-compare"
    : "col-lg-3 col-12";
  const trendChartClasses = props.isComparingResults
    ? "col-xl-6 col-12 order-3 order-xl-2 overview-trending-compare"
    : "col-lg-9 col-12";

  return (
    <div className="row">
      <div className={`overview-trending-charts ${arcReactorClasses}`}>
        <div className="arc-reactor" style={{ alignItems: "center" }}>
          <div className="text-center mb15" style={{ width: "100%" }}>
            <span className="bone">Monthuary&nbsp;Year</span>
          </div>
          <span className="bone skeleton-arc-outer">
            <span className="skeleton-arc-inner" />
          </span>
        </div>
      </div>

      {(props.isComparingResults || null) && (
        <div className={`overview-trending-charts col-xl-3 col-6 order-2 order-xl-3 overview-trending-compare`}>
          <div className="arc-reactor" style={{ alignItems: "center" }}>
            <div className="text-center mb15" style={{ width: "100%" }}>
              <span className="bone">Monthuary&nbsp;Year</span>
            </div>
            <span className="bone skeleton-arc-outer">
              <span className="skeleton-arc-inner" />
            </span>
          </div>
        </div>
      )}
      <div className={`overview-trending-charts trend-chart skeleton-trend ${trendChartClasses}`}>
        <div className="bone" />
      </div>
    </div>
  );
}

export { Charts, SkeletonCharts };
